import type { HomeyAPI, FlowCardCondition } from 'homey-api';

import { BaseApiStore, type FetchArgs } from '../BaseApiStore.ts';
import { __DEV__ } from '../../lib/__dev__.js';

export type FlowCardConditionsProps = {
  [K in keyof FlowCardCondition as FlowCardCondition[K] extends Function
    ? never
    : K]: FlowCardCondition[K];
};

type FlowCardConditionsData = { [key: string]: FlowCardCondition };
type FlowCardConditionsById = { [key: string]: FlowCardConditionsProps };

interface State {
  data: FlowCardConditionsData | null;
  byId: FlowCardConditionsById | null;
  isLoading: boolean;
  error: unknown;
}

export type FlowCardConditionsStoreState = State;

export class FlowCardConditionsStore extends BaseApiStore<State, FlowCardCondition> {
  constructor() {
    super('flow-card-conditions');
  }

  override createInitialState() {
    return {
      data: null,
      byId: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, silent, cache, abortController }: FetchArgs) {
    if (silent === false) {
      this.set(this.createInitialState());
    }

    try {
      await this.connectManager({ api, manager: api.flow, reference: this });

      if (abortController.signal.aborted) return;

      const flowCardConditionsPromise = api.flow.getFlowCardConditions({ $cache: cache });

      const [flowCardConditionsData] = await Promise.all([flowCardConditionsPromise]);

      if (abortController.signal.aborted) return;

      const flowCardConditions = Object.values(flowCardConditionsData);

      const data: FlowCardConditionsData = {};
      const byId: FlowCardConditionsById = {};

      for (const flowCardConditionInstance of flowCardConditions) {
        const flowCardConditionReference = { ...flowCardConditionInstance };

        data[flowCardConditionReference.id] = flowCardConditionInstance;
        byId[flowCardConditionReference.id] = flowCardConditionReference;
      }

      this.set({
        ...this.createInitialState(),
        data: data,
        byId: byId,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) return;

      this.set({
        ...this.createInitialState(),
        isLoading: false,
        error,
      });
    }
  }

  override destroy(reason: string) {
    try {
      const api = this.getApi();
      this.disconnectManager({ api, manager: api.flow, reference: this }).catch(console.error);
    } catch (error) {}
  }

  override onAttached({ api }: { api: HomeyAPI }): void {
    api.flow.on('flowcardcondition.create', this.handleCreate);
    api.flow.on('flowcardcondition.update', this.handleUpdate);
    api.flow.on('flowcardcondition.delete', this.handleDelete);
  }

  override onDetached({ api }: { api: HomeyAPI }): void {
    api.flow.off('flowcardcondition.create', this.handleCreate);
    api.flow.off('flowcardcondition.update', this.handleUpdate);
    api.flow.off('flowcardcondition.delete', this.handleDelete);
  }

  getInstanceFromCache(flowCardCondition: { id: string }) {
    return this.getCache()[flowCardCondition.id];
  }

  getCache(): { [key: string]: FlowCardCondition } {
    const api = this.getApi();
    const cache = api.flow.__cache['flowcardcondition'];
    return cache;
  }

  handleCreate = (createdFlowCardCondition: FlowCardCondition) => {
    this.DEBUG && console.info(`create:flowcardcondition:${createdFlowCardCondition.id}`);
    const state = this.get();
    const flowCardConditionInstance = this.getInstanceFromCache(createdFlowCardCondition);

    if (flowCardConditionInstance == null) return;

    const flowCardConditionReference = { ...flowCardConditionInstance };

    this.set({
      data: {
        ...state.data,
        [createdFlowCardCondition.id]: flowCardConditionInstance,
      },
      byId: {
        ...state.byId,
        [createdFlowCardCondition.id]: flowCardConditionReference,
      },
    });
  };

  handleUpdate = (updatedFlowCardCondition: FlowCardCondition) => {
    this.DEBUG && console.info(`update:flowcardcondition:${updatedFlowCardCondition.id}`);

    this.addToQueue(updatedFlowCardCondition.id, updatedFlowCardCondition);

    this.batch((updateQueue) => {
      const state = this.get();

      const nextById = {
        ...state.byId,
      };

      let updateCount = 0;

      for (const [id, queuedFlowCardCondition] of Object.entries(updateQueue)) {
        const flowCardConditionInstance = this.getInstanceFromCache(queuedFlowCardCondition);

        if (state.data == null) {
          this.DEBUG && console.info(`update:flowcardcondition:missing-data`);
          continue;
        }

        if (flowCardConditionInstance == null || state.data[id] == null) {
          this.DEBUG && console.info(`update:flowcardcondition:unknown:${id}`);
          continue;
        }

        const flowCardConditionReference = { ...flowCardConditionInstance };

        nextById[id] = flowCardConditionReference;

        updateCount++;
      }

      this.DEBUG && console.info(`update:flowcardcondition:batch:count:${updateCount}`);

      updateCount > 0 &&
        this.set({
          byId: nextById,
        });
    });
  };

  handleDelete = (deletedFlowCardCondition: FlowCardCondition) => {
    this.DEBUG && console.info(`delete:flowcardcondition:${deletedFlowCardCondition.id}`);
    const state = this.get();

    if (state.data == null) {
      __DEV__ && console.log(`delete:flowcardcondition:missing-data`);
      return;
    }

    const flowCardConditionInstance = state.data[deletedFlowCardCondition.id];

    if (flowCardConditionInstance == null) return;

    const nextData = { ...state.data };
    const nextById = { ...state.byId };

    delete nextData[flowCardConditionInstance.id];
    delete nextById[flowCardConditionInstance.id];

    this.set({
      data: nextData,
      byId: nextById,
    });
  };

  restore() {
    const cache = this.getCache();
    const advancedFlows = Object.values(cache ?? {});
    const data: FlowCardConditionsData = {};
    const byId: FlowCardConditionsById = {};

    for (const flowCardConditionInstance of advancedFlows) {
      const flowCardConditionReference = { ...flowCardConditionInstance };
      data[flowCardConditionReference.id] = flowCardConditionInstance;
      byId[flowCardConditionReference.id] = flowCardConditionReference;
    }

    this.set({
      data: data,
      byId: byId,
    });
  }
}

export const flowCardConditionsStore = new FlowCardConditionsStore();
